"use client";

import {
  type ReactNode,
  type RefObject,
  useEffect,
  useMemo,
  useState,
} from "react";
import { SelectField } from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
} from "@/components/tool-kit/styles";
import { useToolLocale } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadPng, downloadSvg } from "@/lib/tool-kit/download";
import {
  MM_PER_INCH,
  type Orientation,
  PAGE_CLASS,
  type PageGeometry,
  type PaperSize,
  printablePageCss,
} from "@/lib/tool-kit/printables/page";
import {
  estimateWidth,
  type FontSpec,
  fontStack,
  type TextMeasure,
} from "@/lib/tool-kit/printables/text";

/**
 * English strings every printables widget shares. Each widget spreads this object into
 * its own `<SLUG>_STRINGS`, so a translated content file overrides them through the same
 * `ui` map as the widget's own labels.
 */
export const PRINTABLES_COMMON_STRINGS = {
  paperLabel: "Paper size",
  papers: { letter: "US Letter (8.5 × 11 in)", a4: "A4 (210 × 297 mm)" },
  orientationLabel: "Orientation",
  orientations: { portrait: "Portrait", landscape: "Landscape" },
  print: "Print",
  downloadSvg: "Download page 1 (SVG)",
  downloadPng: "Download page 1 (PNG, 300 dpi)",
  printHint:
    "Prints at true size on the paper chosen above, with a {margin} margin kept clear. In the print dialog choose the same paper, set Scale to 100% (Actual size), and turn off Fit to page and Headers and footers.",
  margins: { letter: "0.5 in", a4: "12 mm" },
  pageCount: "one: {count} page|other: {count} pages",
  pageLabel: "Page {page} of {total}",
} as const;

export type PrintablesCommonStrings = typeof PRINTABLES_COMMON_STRINGS;

/* -------------------------------------------------------------------- measuring */

const CANVAS_EM = 100;

function createCanvasMeasure(): TextMeasure | null {
  if (typeof document === "undefined") {
    return null;
  }
  const context = document.createElement("canvas").getContext("2d");
  if (!context) {
    return null;
  }
  const cache = new Map<string, number>();
  return (text, font) => {
    const key = `${font.kind}|${font.bold ? 1 : 0}|${font.italic ? 1 : 0}|${text}`;
    const hit = cache.get(key);
    if (hit !== undefined) {
      return hit;
    }
    context.font = `${font.italic ? "italic " : ""}${font.bold ? 700 : 400} ${CANVAS_EM}px ${fontStack(font)}`;
    const width = context.measureText(text).width / CANVAS_EM;
    const value = Number.isFinite(width) && width > 0 ? width : 0;
    cache.set(key, value);
    return value;
  };
}

/**
 * A text measurer that is the estimate during the server render and the first client
 * render (so both match), then switches to the browser's real font metrics after mount.
 */
export function useTextMeasure(): TextMeasure {
  const [real, setReal] = useState<TextMeasure | null>(null);
  useEffect(() => {
    setReal(() => createCanvasMeasure());
  }, []);
  return useMemo(() => real ?? estimateWidth, [real]);
}

/* ----------------------------------------------------------------------- pages */

/** Common SVG text attributes for a font spec. */
export function fontAttrs(spec: FontSpec) {
  return {
    fontFamily: fontStack(spec),
    fontWeight: spec.bold ? 700 : 400,
    fontStyle: spec.italic ? ("italic" as const) : ("normal" as const),
  };
}

/**
 * The sheets, one SVG each, stacked on screen and one per physical page in print.
 * `pages` holds each sheet's SVG children, drawn in millimetres.
 */
export function PrintablePages({
  geometry,
  pages,
  label,
  svgRef,
  className = "max-w-xl",
}: {
  geometry: PageGeometry;
  pages: readonly ReactNode[];
  /** The preview's accessible name; the page number is added. */
  label: string;
  /** Receives the first page's SVG for downloads. */
  svgRef?: RefObject<SVGSVGElement | null>;
  /** Tailwind max-width for the screen preview. */
  className?: string;
}) {
  const strings = PRINTABLES_COMMON_STRINGS;
  return (
    <div className="space-y-6 print:space-y-0">
      <style>{printablePageCss(geometry)}</style>
      {pages.map((page, index) => (
        <svg
          aria-label={`${label} — ${t(strings.pageLabel, { page: index + 1, total: pages.length })}`}
          className={`${PAGE_CLASS} mx-auto block h-auto w-full ${className} rounded-sm border border-border bg-white shadow-sm`}
          height={geometry.cssHeight}
          // biome-ignore lint/suspicious/noArrayIndexKey: sheets are positional and never reordered
          key={index}
          ref={index === 0 ? svgRef : undefined}
          role="img"
          viewBox={`0 0 ${geometry.width} ${geometry.height}`}
          width={geometry.cssWidth}
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            fill="#ffffff"
            height={geometry.height}
            width={geometry.width}
          />
          {page}
        </svg>
      ))}
    </div>
  );
}

/** Paper size and orientation selects. */
export function PaperControls({
  idPrefix,
  strings,
  paper,
  orientation,
  onPaper,
  onOrientation,
}: {
  idPrefix: string;
  strings: PrintablesCommonStrings;
  paper: PaperSize;
  orientation?: Orientation;
  onPaper: (paper: PaperSize) => void;
  onOrientation?: (orientation: Orientation) => void;
}) {
  return (
    <>
      <SelectField
        id={`${idPrefix}-paper`}
        label={strings.paperLabel}
        onChange={onPaper}
        options={(["letter", "a4"] as const).map((value) => ({
          value,
          label: strings.papers[value],
        }))}
        value={paper}
      />
      {orientation && onOrientation ? (
        <SelectField
          id={`${idPrefix}-orientation`}
          label={strings.orientationLabel}
          onChange={onOrientation}
          options={(["portrait", "landscape"] as const).map((value) => ({
            value,
            label: strings.orientations[value],
          }))}
          value={orientation}
        />
      ) : null}
    </>
  );
}

/** Print and download buttons, the page count and the true-scale hint. */
export function PrintBar({
  strings,
  paper,
  pageTotal,
  svgRef,
  fileName,
  disabled = false,
  downloads = true,
  extra,
}: {
  strings: PrintablesCommonStrings;
  paper: PaperSize;
  pageTotal: number;
  svgRef: RefObject<SVGSVGElement | null>;
  /** File name without extension for the downloads. */
  fileName: string;
  disabled?: boolean;
  downloads?: boolean;
  extra?: ReactNode;
}) {
  const locale = useToolLocale();
  function save(format: "svg" | "png") {
    const svg = svgRef.current;
    if (!svg) {
      return;
    }
    if (format === "svg") {
      downloadSvg(`${fileName}.svg`, svg);
      return;
    }
    downloadPng(`${fileName}.png`, svg, { scale: 300 / MM_PER_INCH });
  }
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <button
          className={BUTTON_PRIMARY}
          disabled={disabled}
          onClick={() => window.print()}
          type="button"
        >
          {strings.print}
        </button>
        {downloads ? (
          <>
            <button
              className={BUTTON_SECONDARY}
              disabled={disabled}
              onClick={() => save("svg")}
              type="button"
            >
              {strings.downloadSvg}
            </button>
            <button
              className={BUTTON_SECONDARY}
              disabled={disabled}
              onClick={() => save("png")}
              type="button"
            >
              {strings.downloadPng}
            </button>
          </>
        ) : null}
        {extra}
        <span aria-live="polite" className={HINT}>
          {tPlural(strings.pageCount, locale, pageTotal)}
        </span>
      </div>
      <p className={HINT}>
        {t(strings.printHint, { margin: strings.margins[paper] })}
      </p>
    </div>
  );
}
